import React, { useState, useEffect } from "react";
import {
  Navigate,
  useNavigate,
  useLocation,
  Outlet,
} from "react-router-dom";
import { Toast, ToastContainer } from "react-bootstrap";
import {
  APIGetTags,
  APIGetBundles,
  APIGetVisualizations,
  APIGetOrgVisuals,
  setSessionLostCallback,
  APIExternalLogin,
  APIGetUserInfo,
} from "./helper/APIFunctions";

import Header from "./components/Header";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import UseCasesPage from "./pages/UseCasesPage";
import Footer from "./components/Footer";
import WelcomeChatbot from "./components/WelcomeChatbot";
import PowerPointPage from "./pages/PowerPointPage";
import PasswordResetPage from "./pages/PasswordResetPage";
import ProfilePage from "./pages/ProfilePage";
import PricingPage from "./pages/PricingPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ContactPage from "./pages/ContactPage";
import DoiRequestPage from "./pages/DoiRequest.js";
import DashboardPage from "./pages/DashboardPage";
import DeletedVisualsPage from "./pages/DeletedVisualsPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AdminPage from "./pages/AdminPage.js";
import BundlePage from "./pages/BundlesPage.js";
import GNYDM2023Page from "./pages/GNYDM2023Page.js";
import CreatePresentationPage from "./pages/CreatePresentationPage";
import ScrollytellingBuilderPage from "./pages/ScrollytellingBuilderPage";
import ScrollytellingPreviewPage from "./pages/ScrollytellingPreviewPage";
import {
  DisclaimerSigned,
  UserInfo,
  ActiveTour,
  AvailableTags,
  TagLookup,
  StripePromise,
  VisualCache,
  Bundles,
} from "./helper/Context";
import JoyrideWrapper from "./components/JoyrideWrapper";
import TutorialsPage from "./pages/TutorialsPage";
import OrganizationInfoPage from "./pages/OrganizationInfoPage";
import OrganizationJoinPage from "./pages/OrganizationJoinPage";
import MessageCenter from "./components/MessageCenter.js";
import TaggedVisualsPage from "./pages/TaggedVisualsPage";
import AddTagsPage from "./pages/AddTagsPage";
import TagListPage from "./pages/TagListPage";
import { loadStripe } from "@stripe/stripe-js";
import { updateInstitutionProfile, updateMissionStatement } from "./helper/UserSettings";

import {
  isMobile,
  canRestoreVisuals,
  getDefaultPage,
} from "./helper/BusinessLogic";
import {
  isMobile as isMobileDevice
} from "./helper/Utils";
import ScrollToTop from "./components/ScrollToTop";
import DisableScrollRestoration from "./components/DisableScrollRestoration";

// Helper component for protected routes
const ProtectedRoute = ({ userInfo, children }) => {
  if (userInfo === null) {
    return <Navigate to="/home" replace />;
  }
  return children;
};

const App = (props) => {
  const [userInfo, setUserInfo] = useState(props.userInfo);
  const [disclaimerSigned, setDisclaimerSigned] = useState(!!userInfo);
  const [activeTour, setActiveTour] = useState(null);
  const [availableTags, setAvailableTags] = useState(null);
  const [tagLookup, setTagLookup] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [visCache, setVisCache] = useState({});
  const [bundles, setBundles] = useState(null);
  const [cachedVisEmail, setCachedVisEmail] = useState(null);
  const [showSessionLostToast, setShowSessionLostToast] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  let location = useLocation();
  let navigate = useNavigate();

  useEffect(() => {
    setUserInfo(props.userInfo);
    setDisclaimerSigned(!!props.userInfo);
  }, [props.userInfo]);

  // Set up session lost callback for mobile Safari
  useEffect(() => {
    setSessionLostCallback(() => {
      setUserInfo(null);
      setVisCache({});
      setCachedVisEmail(null);
      setShowSessionLostToast(true);
      // Navigate to home page when session is lost
      navigate('/home');
    });
  }, [navigate]);

  useEffect(() => {
    if (userInfo) {
      if (userInfo?.institution_profile){
        updateInstitutionProfile(userInfo.email, userInfo?.institution_profile || '');
      }
      if (userInfo?.mission_statement){
        updateMissionStatement(userInfo.email, userInfo?.mission_statement || '');
      }
    }
  }, [userInfo]);

  useEffect(() => {
    if (!userInfo) {
      setVisCache({});
      setCachedVisEmail(null);
    } else if (userInfo && userInfo.email !== cachedVisEmail) {
      setCachedVisEmail(userInfo.email);
      let temp = {};

      const mobileCheck = isMobileDevice();

      // On mobile devices, we skip loading visuals since cookie-based auth will fail.
      if (mobileCheck) {
        temp["personalVises"] = false; // Indicates load attempted but failed
        temp["orgVises"] = false;
        setVisCache(temp);
        return;
      }

      const fetchAllVisuals = (body) => {
        const visList = body.visuals || body;
        for (const v of visList) {
          v.isOrgVis = false;
          temp[v.id] = v;
        }
        for (const vid of body.videos || []) {
          vid.type = "video";
          temp[vid.id] = vid;
        }
        temp["personalVises"] = true;

        if (userInfo?.organization_permissions) {
          APIGetOrgVisuals(
            (body2) => {
              const visList2 = body2.visuals || body2; // Renamed to avoid conflict
              for (const v of visList2) {
                v.isOrgVis = true;
                temp[v.id] = v;
              }
              for (const vid of body2.videos || []) {
                vid.type = "video";
                vid.isOrgVis = true;
                temp[vid.id] = vid;
              }
              temp["orgVises"] = true;
              setVisCache(temp);
            },
            () => { temp["orgVises"] = false; setVisCache(prev => ({ ...prev, ...temp, orgVises: false })); } // Ensure visCache is updated on failure
          );
        } else {
          temp["orgVises"] = true; // Assuming if no org_permissions, orgVises is true (no org visuals to fetch)
          setVisCache(temp);
        }
      };

      APIGetVisualizations(
        fetchAllVisuals,
        () => {
          temp["personalVises"] = false;
          setVisCache(prev => ({ ...prev, personalVises: false }));
        },
        visCache // Pass the current visCache here
      );
    }
  }, [userInfo, cachedVisEmail]);

  useEffect(() => {
    APIGetTags(
      (res) => {
        res.sort((a, b) =>
          a.title.toLowerCase().localeCompare(b.title.toLowerCase())
        );
        setAvailableTags(res);
        const dict = {};
        res.forEach((tag) => (dict[tag.slug] = tag));
        setTagLookup(dict);
      },
      () => { }
    );
    APIGetBundles(
      (res) => {
        setBundles(res);
      },
      () => { }
    );
    setStripePromise(loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    window?.google?.accounts?.id?.initialize({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      callback: (response) => {
        const requestType = (window._snorkle || {} ).oauthRequestType  || "login-modal";
        let targetElement = null;
        if (requestType === "login-modal") {
          targetElement = document.getElementById("login-modal");
        }
        else if (requestType === "organization-join") {
          targetElement = document.getElementById("organization-join-modal");
        }
        else if (requestType === "billing-details") {
          targetElement = document.getElementById("billing-details-container");
        }
        else if (requestType === "subscription-modify") {
          targetElement = document.getElementById("subscription-modify-confirm-modal");
        }
        if (!response?.credential) {
          targetElement?.dispatchEvent(new CustomEvent("oauth-login-error"));
          return;
        }
        targetElement?.dispatchEvent(new CustomEvent("oauth-login-started")); 
        APIExternalLogin(response.credential, "google", ()=>{
          if (requestType === "login-modal"){
            APIGetUserInfo((res)=>{
              res.set_from_oauth = true;
              setUserInfo(res);
              setDisclaimerSigned(!!res);
              targetElement?.dispatchEvent(new CustomEvent("oauth-login-success"));
            }, () => {targetElement?.dispatchEvent(new CustomEvent("oauth-login-error"));});
          }
          else if (requestType === "organization-join"){
            APIGetUserInfo((res)=>{
              res.set_from_oauth = true;
              setUserInfo(res);
              setDisclaimerSigned(!!res);
              targetElement?.dispatchEvent(new CustomEvent("oauth-login-success"));
            }, () => {targetElement?.dispatchEvent(new CustomEvent("oauth-login-error"));});
          }
          else if (requestType === "billing-details"){
            targetElement?.dispatchEvent(new CustomEvent("oauth-login-success"));
          }
          else if (requestType === "subscription-modify"){
            targetElement?.dispatchEvent(new CustomEvent("oauth-login-success"));
          }
        }, () => {});
      },
      auto_select: false,
    });
  }, []);
  // Auto-hide header for molecular-viewer, organ-segmentation, and welcome pages
  useEffect(() => {
    const glassPages = ['/molecular-viewer', '/organ-segmentation', '/welcome'];
    const isGlassPage = glassPages.some(page => location.pathname.includes(page));
    
    if (!isGlassPage) {
      setShowHeader(true); // Always show on other pages
    } else {
      setShowHeader(false); // Start in glass mode
    }
  }, [location.pathname]);

  // Determine page type for chatbot
  const getChatbotPageType = () => {
    if (location.pathname.includes('/dashboard')) return 'dashboard';
    return 'homepage';
  };

  return (
    <DisclaimerSigned.Provider
      value={{ disclaimerSigned, setDisclaimerSigned }}
    >
      <UserInfo.Provider value={{ userInfo, setUserInfo }}>
        <ActiveTour.Provider value={{ activeTour, setActiveTour }}>
          <AvailableTags.Provider value={{ availableTags, setAvailableTags }}>
            <TagLookup.Provider value={{ tagLookup, setTagLookup }}>
              <VisualCache.Provider value={{ visCache, setVisCache }}>
                <Bundles.Provider value={{ bundles, setBundles }}>
                  <StripePromise.Provider value={stripePromise}>
                    <DisableScrollRestoration />
                    <ScrollToTop />
                    <div className="main-container">
                      {location.pathname !== '/welcome' && location.pathname !== '/channel-preview' && (
                        <div 
                          style={{
                            opacity: (location.pathname === '/molecular-viewer' || location.pathname.includes('/organ-segmentation')) ? (showHeader ? 1 : 0.3) : 1,
                            filter: (location.pathname === '/molecular-viewer' || location.pathname.includes('/organ-segmentation')) ? (showHeader ? 'none' : 'saturate(0) brightness(1.3) contrast(0.9)') : 'none',
                            backdropFilter: (location.pathname === '/molecular-viewer' || location.pathname.includes('/organ-segmentation')) ? (showHeader ? 'blur(0px)' : 'blur(8px) brightness(1.1)') : 'none',
                            WebkitBackdropFilter: (location.pathname === '/molecular-viewer' || location.pathname.includes('/organ-segmentation')) ? (showHeader ? 'blur(0px)' : 'blur(8px) brightness(1.1)') : 'none',
                            transition: 'opacity 0.2s ease-in-out, filter 0.2s ease-in-out, backdrop-filter 0.2s ease-in-out',
                            position: (location.pathname === '/molecular-viewer' || location.pathname.includes('/organ-segmentation')) ? 'fixed' : 'relative',
                            top: 0,
                            left: 0,
                            right: 0,
                            width: '100%',
                            zIndex: 1000,
                            pointerEvents: 'auto',
                            backgroundColor: (location.pathname === '/molecular-viewer' || location.pathname.includes('/organ-segmentation')) ? (showHeader ? 'rgba(13, 17, 23, 0.95)' : 'rgba(255, 255, 255, 0.08)') : 'transparent',
                            borderBottom: (location.pathname === '/molecular-viewer' || location.pathname.includes('/organ-segmentation')) ? (showHeader ? 'none' : '1px solid rgba(255, 255, 255, 0.1)') : 'none'
                          }}
                          onMouseEnter={() => {
                            if (location.pathname === '/molecular-viewer' || location.pathname.includes('/organ-segmentation')) {
                              setShowHeader(true);
                            }
                          }}
                          onMouseLeave={() => {
                            if (location.pathname === '/molecular-viewer' || location.pathname.includes('/organ-segmentation')) {
                              setShowHeader(false);
                            }
                          }}
                        >
                          <Header />
                        </div>
                      )}
                      <JoyrideWrapper />
                      <div className="flex-grow-1" id="main-body">
                        <Outlet /> {/* Routed content will be rendered here */}
                      </div>
                      <MessageCenter />
                      {/* Hide footer on molecular-viewer, organ-segmentation, channel-preview, and welcome pages (they're fullscreen) */}
                      {location.pathname !== '/molecular-viewer' && !location.pathname.includes('/organ-segmentation') && location.pathname !== '/welcome' && location.pathname !== '/channel-preview' && (
                        <footer className="ps-4 pe-3 footer mt-auto">
                          <Footer />
                        </footer>
                      )}
                      
                      {/* Session lost notification for mobile Safari */}
                      <ToastContainer
                        className="p-3"
                        position="top-center"
                        style={{ zIndex: 1060 }}
                      >
                        <Toast
                          onClose={() => setShowSessionLostToast(false)}
                          show={showSessionLostToast}
                          delay={5000}
                          autohide
                          bg="warning"
                        >
                          <Toast.Header closeButton={true}>
                            <strong className="me-auto">Session Expired</strong>
                          </Toast.Header>
                          <Toast.Body>
                            Your session has expired. Please log in again.
                          </Toast.Body>
                        </Toast>
                      </ToastContainer>
                    </div>
                    
                    {/* Global Chatbot - shows on all pages except welcome screen */}
                    {location.pathname !== '/welcome' && (
                      <WelcomeChatbot 
                        pageType={getChatbotPageType()}
                        isLoggedIn={!!userInfo}
                      />
                    )}
                  </StripePromise.Provider>
                </Bundles.Provider>
              </VisualCache.Provider>
            </TagLookup.Provider>
          </AvailableTags.Provider>
        </ActiveTour.Provider>
      </UserInfo.Provider>
    </DisclaimerSigned.Provider>
  );
};

export default App;
